import {
  ADD_ARTICLE,
  ADD_VISIT,
  ADD_PATIENT,
  ADD_ADMIN,
  BACKEND_URL,
  AUTH_SUCCESS,
  ADD_PATIENTEMAILS,
  NEWSLETTERS,
} from "../constants/action-types";
import moment from "moment";
import client from "../api/client";

export function addArticle(payload) {
  return { type: ADD_ARTICLE, payload };
}

export function addVisit(payload) {
  return { type: ADD_VISIT, payload };
}

export function addPatient(payload) {
  return { type: ADD_PATIENT, payload };
}

export function addPatientEmails(payload) {
  return { type: ADD_PATIENTEMAILS, payload };
}

export function newsletters(payload) {
  return { type: NEWSLETTERS, payload };
}

export function getBackendURL(payload) {
  return { type: BACKEND_URL, payload };
}

export function successfulAuth(email, adminId, name, permissions) {
  return function (dispatch) {
    console.log("AUTH", email, adminId, name);
    dispatch(setAuth(email, adminId, name, permissions));
  };
}

export function successfulAuthOut() {
  // console.log("log out redux")
  return function (dispatch) {
    dispatch(setAuthOut());
  };
}

export function setAuthOut() {
  // console.log("log out redux auth")
  return {
    type: AUTH_SUCCESS,
    payload: { username: "", adminId: "", name: "", loggedIn: false },
  };
}

export function setAuth(email, adminId, name, permissions) {
  // console.log("log in redux auth")
  return {
    type: AUTH_SUCCESS,
    payload: {
      username: email,
      adminId: adminId,
      name: name,
      loggedIn: true,
      permissions: permissions,
    },
  };
}

// The backend caps a calendar query at 90 days and now rejects a missing range
// outright — the old "no dates returns every visit ever" branch is gone.
export const MAX_RANGE_DAYS = 90;

// Default window for views that used to call fetchVisits() with no arguments —
// services.js and patients.js both did, which hit the old "return every visit
// ever recorded" branch. Kept just under MAX_RANGE_DAYS so it cannot trip the
// server's cap.
const DEFAULT_RANGE = {
  start: () => moment().subtract(30, "days").format("YYYY-MM-DD"),
  end: () => moment().add(59, "days").format("YYYY-MM-DD"),
};

/**
 * Load visits for a date range.
 *
 * This endpoint now requires a staff session — it was the one action in this
 * file that sent no Authorization header, which is why it had to be public and
 * why it leaked patient records. The kiosk no longer uses it at all; see
 * api/kiosk.js.
 */
export function fetchVisits(startDate, endDate, getAddress = false) {
  const start = startDate || DEFAULT_RANGE.start();
  const end = endDate || DEFAULT_RANGE.end();

  return function (dispatch) {
    return client
      .get(`/visits/get/${start}/${end}/${getAddress}`)
      .then(({ data }) => {
        dispatch(setVisits(data));
        return data;
      })
      .catch((error) => {
        // A 403 here means the account may not request addresses; retry without
        // them rather than leaving the calendar empty.
        if (error.isForbidden && getAddress) {
          return client
            .get(`/visits/get/${start}/${end}/false`)
            .then(({ data }) => {
              dispatch(setVisits(data));
              return data;
            });
        }
        throw error;
      });
  };
}

function setVisits(data) {
  return {
    type: ADD_VISIT,
    payload: data,
  };
}

export function fetchPatients() {
  return function (dispatch) {
    return client.get("/patients/get").then(({ data }) => {
      dispatch(setPatients(data));
    });
  };
}

function setPatients(data) {
  return {
    type: ADD_PATIENT,
    payload: data,
  };
}

// Administrator-only on the backend; a practitioner gets a 403 here. Callers
// should treat an empty list as "not permitted" rather than "no patients".
export function fetchPatientEmails() {
  return function (dispatch) {
    return client
      .get("/patients/getemails")
      .then(({ data }) => {
        dispatch(setPatientEmails(data));
      })
      .catch((error) => {
        if (error.isForbidden) {
          dispatch(setPatientEmails([]));
          return;
        }
        throw error;
      });
  };
}

function setPatientEmails(data) {
  return {
    type: ADD_PATIENTEMAILS,
    payload: data,
  };
}

// Best-effort fetch: callers treat an empty list as "not available" rather than
// an error.
//
// skipAuthRedirect is required. This runs on pages reachable without a session,
// where a 401 is the expected answer — without it the global handler treats that
// as an expired session and redirects to /login, throwing the user off the
// public page they asked for.
export function fetchAdmins() {
  return async function (dispatch) {
    try {
      const { data } = await client.get("/admins/get", {
        skipAuthRedirect: true,
      });
      dispatch(setAdmins(data));
    } catch (error) {
      if (error.status !== 401 && error.status !== 403) {
        console.error("Error fetching admins:", error.message);
      }
    }
  };
}

function setAdmins(data) {
  return {
    type: ADD_ADMIN,
    payload: data,
  };
}

// Administrator-only on the backend.
export function fetchNewsletters() {
  return function (dispatch) {
    return client
      .get("/newsletters/get")
      .then(({ data }) => {
        dispatch(setNewsletters(data));
      })
      .catch((error) => {
        if (error.isForbidden) {
          dispatch(setNewsletters([]));
          return;
        }
        throw error;
      });
  };
}

function setNewsletters(data) {
  return {
    type: NEWSLETTERS,
    payload: data,
  };
}
